import { useEffect, useState } from 'react'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { useCurrentUser } from '@/features/auth/state/auth'

import { DownloadIcon, PlusIcon, Edit2, Save, Lock } from 'lucide-react'
import { useCreatedCredit, useFetchCreditByID, useUpdateCredit } from '@/lib/hooks/previdenciario'
import ExcelJS from 'exceljs'
import { saveAs } from 'file-saver'
import { useFetchClientByCnpjCpf } from '@/features/client/services'
import { useToast } from '@/lib/hooks/use-toast'
import DetalhesDeCreditoDeleteBtn from './detalhes-de-credito-delete-btn'
import { MaskInputDin } from '@/components/ui/mask-input-din'
import { MesAnoInput } from '@/components/form/input/date-mask-input'

import { FileManager } from '@/components/form/anexar'
import { useApuracaoSelecionada } from '../../hooks/seApuracaoSelecionada'
import { formatCurrency } from '../../utils'

type Credit = {
  id: string
  anoCompetencia: string
  mesCompetencia: string
  comissao: number
  createdDate: number
  creditOrigin: number
  creditReal: number
  isPago: boolean
  selic: number
  compensacao: number
  id_apuracao_competencia?: string
}

type Boleto = {
  id: string
  numeroBoleto: string
  competencia: string
  valor: number
  nossoNumero: string
  dataEmissao: string
  dataVencimento: string
  status: string
  usuario: string
  dataAcao: string
}

export default function DetalhesDeCredito({
  cnpj,
  id_product,
  onChange
}: {
  cnpj: string
  id_product: string
  onChange?: () => void
}) {
  const [boletoHistory, setBoletoHistory] = useState<Boleto[]>([])
  const [editingRows, setEditingRows] = useState<Set<string>>(new Set())
  const { data: clientData } = useFetchClientByCnpjCpf(cnpj)

  const [isModalOpen, setIsModalOpen] = useState<{
    open: boolean
    id: string | null
  }>({
    open: false,
    id: null
  })

  const { apuracaoSelecionada } = useApuracaoSelecionada()

  const isEditable = apuracaoSelecionada?.isModifica === true
  const id_apuracao = apuracaoSelecionada?.id
  const competencias = apuracaoSelecionada?.competencias
  const competenciaSelecionada = Array.isArray(competencias) ? competencias[0] : competencias
  const id_apuracao_competencia = competenciaSelecionada?.id_apuracao_competencia ?? ''

  const user = useCurrentUser()
  const { toast } = useToast()
  const { mutate: updateCredit } = useUpdateCredit()
  const { mutate: createdCredit } = useCreatedCredit()
  const { data: creditData, refetch } = useFetchCreditByID(id_apuracao ?? '')

  const [creditControls, setCreditControls] = useState<Credit[]>([])

  useEffect(() => {
    if (creditData) {
      setCreditControls(creditData)
    }
  }, [creditData])

  const handleToggleEdit = (id: string) => {
    setEditingRows(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const handleCreditControlChange = (id: string, field: string, value: boolean | string | number) => {
    setCreditControls(prevControls =>
      prevControls.map(control => {
        if (control.id === id) {
          const newControl = { ...control }

          if (field === 'isPago' && typeof value === 'boolean') {
            newControl.isPago = value
          } else if (field === 'competencia' && typeof value === 'string') {
            // Fatiamos o valor digitado
            const mes = value.slice(0, 2)
            const ano = value.slice(2)

            // Atualizamos as propriedades separadamente
            newControl.mesCompetencia = mes
            newControl.anoCompetencia = ano
          } else if (field === 'creditOrigin') {
            newControl.creditOrigin = value as number
          } else if (field === 'creditReal') {
            newControl.creditReal = value as number
          }

          const creditOrigin = Number(newControl.creditOrigin) || 0
          const creditReal = Number(newControl.creditReal) || 0
          const compensacao = creditOrigin - creditReal
          const percentualComissao = Number(clientData?.comissaoTenant) || 0

          newControl.compensacao = compensacao
          newControl.comissao = compensacao * (percentualComissao / 100)

          return newControl
        }
        return control
      })
    )
    onChange && onChange()
  }

  const handleSaveCreditControl = (credit: Credit) => {
    const normalizedApuracaoCompetencia =
      credit.id_apuracao_competencia?.trim() || id_apuracao_competencia.trim() || undefined

    const body = {
      id: credit.id,
      anoCompetencia: credit.anoCompetencia,
      mesCompetencia: credit.mesCompetencia,
      creditOrigin: Number(credit.creditOrigin),
      creditReal: Number(credit.creditReal),
      comissao: Number(credit.comissao),
      compensacao: Number(credit.compensacao),
      isPago: credit.isPago,
      idTenant: user?.id_tenant ?? '',
      id_apuracao_competencia: normalizedApuracaoCompetencia,
      comissao_porcentagem: clientData.comissaoTenant
    }

    updateCredit(
      { body },
      {
        onSuccess: () => {
          toast({ variant: 'default', description: 'O crédito foi salvo com sucesso!' })
          setEditingRows(prev => {
            const newSet = new Set(prev)
            newSet.delete(credit.id)
            return newSet
          })
        },
        onError: () => {
          toast({ variant: 'destructive', description: 'Houve um erro ao salvar o crédito.' })
        }
      }
    )
  }

  const handleAddCreditControl = () => {
    createdCredit(
      {
        body: {
          id_client: cnpj,
          id_product: id_product,
          id_apuracao: id_apuracao ?? '',
          id_apuracao_competencia: id_apuracao_competencia.trim() || undefined
        }
      },
      {
        onSuccess: () => {
          toast({ description: 'O crédito foi salvo com sucesso!' })
          refetch()
        },
        onError: () => {
          toast({ variant: 'destructive', description: 'Houve um erro ao salvar o crédito.' })
        }
      }
    )
    onChange && onChange()
  }

  const handleDownloadExcel = async (creditControls: any[], cnpj: string) => {
    if (!creditControls || creditControls.length === 0) {
      toast({ variant: 'destructive', description: 'Não há dados para exportar.' })
      return
    }

    const workbook = new ExcelJS.Workbook()
    const worksheet = workbook.addWorksheet('Créditos')

    worksheet.addRow([`Detalhes de Crédito - CNPJ: ${cnpj}`])
    worksheet.addRow([])

    worksheet.addRow([
      'Competência',
      'Comissão (R$)',
      'Crédito Original (R$)',
      'Crédito Real (R$)',
      'Compensação (R$)',
      'Status'
    ])

    creditControls.forEach(control => {
      worksheet.addRow([
        `${control.mesCompetencia}/${control.anoCompetencia}`,
        formatCurrency(control.comissao),
        formatCurrency(control.creditOrigin),
        formatCurrency(control.creditReal),
        formatCurrency(control.compensacao),
        control.isPago
      ])
    })

    worksheet.columns = [{ width: 15 }, { width: 15 }, { width: 20 }, { width: 15 }, { width: 20 }, { width: 12 }]

    const buffer = await workbook.xlsx.writeBuffer()
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    saveAs(blob, `detalhes_credito_${cnpj}.xlsx`)
  }

  const handleModal = (id: string) => {
    setIsModalOpen({ open: true, id })
  }

  const handleGenerateBoleto = (competencia: string, valor: number) => {
    const newBoleto: Boleto = {
      id: `${Date.now()}-${Math.random().toString(36)}`,
      numeroBoleto: Math.floor(100000000 + Math.random() * 900000000).toString(),
      competencia,
      valor,
      nossoNumero: Math.floor(10000000 + Math.random() * 90000000).toString(),
      dataEmissao: new Date().toLocaleDateString(),
      dataVencimento: new Date(new Date().setDate(new Date().getDate() + 30)).toLocaleDateString(),
      status: 'Emitido',
      usuario: 'Usuário Atual',
      dataAcao: new Date().toLocaleString()
    }
    setBoletoHistory([newBoleto, ...boletoHistory])
    toast({ description: 'Boleto gerado com sucesso!' })
    onChange && onChange()
  }

  const isAdmin = user?.typeUser === 'ADMIN'

  return (
    <div className="bg-white rounded-lg shadow-lg mt-6 overflow-hidden mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r  p-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl text-foreground font-bold">Detalhes do Crédito</h2>
          </div>
          <div className="space-x-3">
            {isAdmin && (
              <>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleDownloadExcel(creditControls, cnpj)}
                  className="bg-blue-600 hover:bg-blue-700 text-white border-blue-700"
                >
                  <DownloadIcon className="h-4 w-4 mr-2" />
                  Exportar
                </Button>

                <Button
                  onClick={handleAddCreditControl}
                  size="sm"
                  disabled={!isEditable}
                  className="bg-green-500 hover:bg-green-600 text-white"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  Adicionar
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Table Container */}
      <div className="p-6">
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <div className="max-h-[500px] overflow-y-auto">
              <Table className="w-full min-w-[1200px]">
                <TableHeader className="bg-gray-50 sticky top-0">
                  <TableRow>
                    <TableHead className="text-foreground font-semibold w-16">STATUS</TableHead>
                    <TableHead className="text-foreground font-semibold w-28">COMPETÊNCIA</TableHead>
                    <TableHead className="text-foreground font-semibold w-36">CRÉDITO ORIGINAL</TableHead>
                    <TableHead className="text-foreground font-semibold w-32">CRÉDITO REAL</TableHead>
                    <TableHead className="text-foreground font-semibold w-36">COMPENSAÇÃO</TableHead>
                    <TableHead className="text-foreground font-semibold w-32">COMISSÃO</TableHead>
                    {isAdmin && <TableHead className="text-foreground font-semibold w-24">ANEXOS</TableHead>}
                    {isAdmin && <TableHead className="text-foreground font-semibold w-24">BOLETO</TableHead>}
                    {isAdmin && <TableHead className="text-foreground font-semibold w-32">AÇÕES</TableHead>}
                  </TableRow>
                </TableHeader>

                <TableBody>
                  {creditControls.map((control, index) => {
                    const isEditing = editingRows.has(control.id)
                    const rowBg = index % 2 === 0 ? 'bg-white' : 'bg-gray-50'

                    return (
                      <TableRow key={control.id} className={`${rowBg} hover:bg-blue-50 transition-colors`}>
                        {/* Status */}
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Checkbox
                              checked={control.isPago}
                              onCheckedChange={checked =>
                                isAdmin && handleCreditControlChange(control.id, 'isPago', !!checked)
                              }
                              disabled={!isAdmin}
                              className="data-[state=checked]:bg-green-500"
                            />
                            <span
                              className={`text-xs px-2 py-1 rounded-full font-medium ${
                                control.isPago ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                              }`}
                            >
                              {control.isPago}
                            </span>
                          </div>
                        </TableCell>

                        {/* Competência - editável quando em modo de edição */}
                        <TableCell>
                          {isEditing && isAdmin && isEditable ? (
                            <MesAnoInput
                              value={`${control.mesCompetencia || ''}${control.anoCompetencia ? '/' + control.anoCompetencia : ''}`}
                              onChange={(value: string | number | boolean) =>
                                handleCreditControlChange(control.id, 'competencia', value)
                              }
                            />
                          ) : (
                            <div className="bg-input px-3 py-2 rounded text-center font-mono">
                              {control.mesCompetencia || '--'}/{control.anoCompetencia || '----'}
                            </div>
                          )}
                        </TableCell>

                        {/* Crédito Original - editável */}
                        <TableCell>
                          {isEditing && isAdmin && isEditable ? (
                            <MaskInputDin
                              value={Number(control.creditOrigin) || 0}
                              onChange={(value: string | number | boolean) =>
                                handleCreditControlChange(control.id, 'creditOrigin', value)
                              }
                              className="font-medium"
                            />
                          ) : (
                            <div className="px-3 py-2 bg-input rounded text-foreground font-medium text-center">
                              {formatCurrency(control.creditOrigin ?? 0)}
                            </div>
                          )}
                        </TableCell>

                        {/* Crédito Real - editável */}
                        <TableCell>
                          {isEditing && isAdmin && isEditable ? (
                            <MaskInputDin
                              value={Number(control.creditReal) || 0}
                              onChange={(value: string | number | boolean) =>
                                handleCreditControlChange(control.id, 'creditReal', value)
                              }
                              className="font-medium"
                            />
                          ) : (
                            <div className="px-3 py-2 bg-input rounded text-foreground text-center font-medium">
                              {formatCurrency(control.creditReal ?? 0)}
                            </div>
                          )}
                        </TableCell>

                        {/* Compensação - sempre readonly */}
                        <TableCell>
                          <div className="px-3 py-2 bg-input rounded text-foreground font-medium text-center">
                            {formatCurrency(control.compensacao ?? 0)}
                          </div>
                        </TableCell>

                        {/* Comissão - sempre readonly */}
                        <TableCell>
                          <div className="px-3 py-2 bg-input rounded text-[(--text2-color)] font-medium text-center">
                            {formatCurrency(control.comissao ?? 0)}
                          </div>
                        </TableCell>

                        {/* Anexos */}
                        {isAdmin && (
                          <TableCell>
                            <Button
                              onClick={() => handleModal(control.id)}
                              size="sm"
                              variant="outline"
                              disabled={!isEditable}
                              className="w-full bg-primary hover:bg-primary-hover text-white"
                            >
                              Anexar
                            </Button>
                          </TableCell>
                        )}

                        {/* Boleto */}
                        {isAdmin && (
                          <TableCell>
                            <Button
                              onClick={() =>
                                handleGenerateBoleto(
                                  `${control.mesCompetencia}/${control.anoCompetencia}`,
                                  control.compensacao
                                )
                              }
                              size="sm"
                              disabled={control.isPago}
                              className="w-full bg-primary hover:bg-primary-hover text-white"
                            >
                              Gerar
                            </Button>
                          </TableCell>
                        )}

                        {/* Ações */}
                        {isAdmin && (
                          <TableCell>
                            <div className="flex space-x-1">
                              {isEditing ? (
                                <Button
                                  onClick={() => handleSaveCreditControl(control)}
                                  size="sm"
                                  disabled={!isEditable}
                                  className="bg-green-500 hover:bg-green-600 text-white px-2"
                                >
                                  <Save className="h-3 w-3" />
                                </Button>
                              ) : (
                                <Button
                                  onClick={() => handleToggleEdit(control.id)}
                                  size="sm"
                                  variant="outline"
                                  disabled={!isEditable}
                                  className="px-2"
                                >
                                  {isEditable ? <Edit2 className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                                </Button>
                              )}
                              <DetalhesDeCreditoDeleteBtn
                                creditId={control.id}
                                isDisabled={!isAdmin || !isEditable}
                                onSuccess={(id: string) => {
                                  setCreditControls(prevControls => prevControls.filter(control => control.id !== id))
                                  toast({ description: 'Crédito excluído com sucesso!' })
                                  onChange && onChange()
                                }}
                              />
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>

        {creditControls.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <div className="text-4xl mb-4">📊</div>
            <p className="text-lg">Nenhum crédito encontrado</p>
            <p className="text-sm">Adicione um novo crédito para começar</p>
          </div>
        )}
      </div>

      {/* Modal de Anexo */}
      {isModalOpen.open && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-lg w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Anexar Arquivo</h2>
            </div>
            <FileManager
              id={isModalOpen.id || ''}
              type="credit"
              id_client={cnpj}
              id_apuracao={id_apuracao ?? ''}
              id_product={id_product ?? ''}
              id_apuracao_competencia={id_apuracao_competencia}
              onClose={() => setIsModalOpen({ open: false, id: null })}
            />
          </div>
        </div>
      )}
    </div>
  )
}
